import { inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { AppStore } from '@app/stores/appStore';

@Injectable({ providedIn: 'root' })
export class MaintenanceService implements OnDestroy {
    private appStore = inject(AppStore);
    /** Signal holding current maintenance mode state (internal to service for polling logic). */
    public maintenanceMode: WritableSignal<boolean> = signal<boolean>(false);
    private configCheckInterval = 5000;
    private configSubscription: Subscription;
    private previousUrl: string = '/';

    constructor(
        private http: HttpClient,
        private router: Router
    ) {
        this.startConfigCheck();

        // Store the current URL before maintenance redirects
        this.router.events.subscribe(event => {
            if (event.constructor.name === 'NavigationEnd') {
                if (
                    !this.maintenanceMode() &&
                    !this.router.url.includes('/maintenance')
                ) {
                    this.previousUrl = this.router.url;
                }
            }
        });
    }

    private startConfigCheck(): void {
        this.configSubscription = interval(this.configCheckInterval).subscribe(
            () => this.checkConfig()
        );
    }

    private async checkConfig(): Promise<void> {
        try {
            const config = await firstValueFrom(
                this.http.get<any>(
                    `assets/config.json?v=${new Date().getTime()}`
                )
            );

            let maintenance = config.maintenance;
            if (maintenance === '${MAINTENANCE}') {
                maintenance = environment.maintenance;
            } else {
                maintenance = maintenance === 'true';
            }

            if (this.maintenanceMode() !== maintenance) {
                this.setMaintenanceMode(maintenance);

                if (maintenance) {
                    this.router.navigate(['/maintenance']);
                } else {
                    // Return to previous page or default route
                    this.router.navigate([this.previousUrl || '/']);
                }
            }
        } catch (error) {
            console.error('Error checking maintenance config:', error);
        }
    }

    setMaintenanceMode(maintenance: boolean): void {
        this.maintenanceMode.set(maintenance);
        this.appStore.setMaintenanceMode(maintenance);
    }

    getPreviousUrl(): string {
        return this.previousUrl;
    }

    ngOnDestroy() {
        if (this.configSubscription) {
            this.configSubscription.unsubscribe();
        }
    }
}
